import Image from 'next/legacy/image'
import { Trans, useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import Head from '@/components/head'

import avatar0 from './avatar0@2x.png'
import avatar1 from './avatar1@2x.png'
import heroBanner from './lbrothers-banner.png'
import lBrothersLogo from './lbrothers-logo.png'
import lBrothersQr from './lbrothers-qrcode.png'

const { BASE_URL } = ENV_VARS

const BannerWrapper = styled.div`
  text-align: center;
  margin-bottom: 30px;
  height: 750px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: 700px) {
    height: auto;
  }
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (max-width: 700px) {
    padding-left: 25px;
    padding-right: 25px;
    padding-top: 60px;
    padding-bottom: 60px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
`

const CompanyLogoWrapper = styled.div`
  margin: 0 auto 30px;
  height: 120px;
  width: 72px;
`

const HorizontalRule = styled.div`
  width: 380px;
  margin-bottom: 40px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.6);

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 280px;
  }
`

const Heading = styled.h1`
  color: ${colors.white};
  font-size: 20px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin: 0 0 32px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
`

const Description = styled.p`
  color: ${colors.white};
  font-size: 17px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0.23px;
  margin: 0 auto;
  max-width: 1000px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
  @media only screen and (max-width: ${breakpoints.desktop}) {
    margin: 0 50px;
  }
  @media only screen and (max-width: 700px) {
    margin: 0 20px;
    font-size: 14px;
  }

  small {
    font-size: ${fontSizes.small};
  }
`

const ContactInfo = styled.p`
  color: ${colors.white};
  font-size: 22px;
  line-height: 30px;
  font-weight: 400;
  letter-spacing: 0.23px;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.5);
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const LandlordsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 95%;
  max-width: 1075px;
  margin: 10px auto 50px;
`

const AvatarsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: none;
  width: 100%;
  margin-top: 10px;
  @media screen and (max-width: 700px) {
    flex-wrap: wrap;
  }
`

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 10px;
`
const AvatarTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const AvatarText = styled.p`
  color: ${colors.white};
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  line-height: 19px;
  letter-spacing: 0.23px;
  text-align: left;
  margin-bottom: 4px;
  margin-left: 20px;
`

const AvatarRole = styled(AvatarText)`
  font-size: 14px;
  line-height: 16px;
  width: 250px;
  @media screen and (max-width: 700px) {
    font-size: 12px;
  }
`

const QrCodeTop = css`
  position: absolute;
  top: 990px;
`

const QrCodeBottom = css`
  position: absolute;
  bottom: 20px;
`

const QrCode = styled.div`
  position: fixed;
  bottom: 10px;
  right: 10px;
  z-index: 100;
  border-radius: 5px;
  border: 5px solid ${colors.white};
  background-color: ${colors.white};
  box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.23);

  ${props => (props.positionBottom ? QrCodeBottom : null)};
  ${props => (props.positionTop ? null : QrCodeTop)};

  @media screen and (max-width: 890px) {
    display: none;
  }
`

const QrCodeImageWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 191px;
`

const LBrothersProfile = ({ footerOnScreen, listingsOnScreen, metadata }) => {
  const { t } = useTranslation('lbrothers')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const profile = {
    banner: heroBanner.src,
    title: t('lbrothers:L Brothers Vancouver Group'),
    pageDescription: t('lbrothers:description.meta'),
    url: '/company/lbrothers',
    agents: [
      {
        name: 'Leo Zheng',
        roles: [
          <AvatarRole key="0">
            {t('lbrothers:Senior Real Estate Consultant')}
          </AvatarRole>,
          <AvatarRole key="1">
            {t('lbrothers:Senior Property Manager')}
          </AvatarRole>
        ],
        image: avatar0
      },
      {
        name: 'Leonard Zheng',
        roles: [
          <AvatarRole key="2">
            {t('lbrothers:Senior Real Estate Consultant')}
          </AvatarRole>,
          <AvatarRole key="3">
            {t('lbrothers:Senior Property Manager')}
          </AvatarRole>
        ],
        image: avatar1
      }
    ]
  }

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        meta={{
          image: `${BASE_URL}/images/l-brothers-banner.png`,
          imageAlt: t('lbrothers:Senior Real Estate Consultant')
        }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper>
        <Image
          layout="fill"
          priority
          src={profile.banner}
          alt="L Brothers"
          sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
        />
        <Container>
          <ContentContainer>
            <CompanyLogoWrapper>
              <Image
                src={lBrothersLogo}
                alt=""
                width={156}
                height={260}
                placeholder="blur"
              />
            </CompanyLogoWrapper>
            <HorizontalRule />
            <Heading>L Brothers Vancouver Group</Heading>
            <Trans i18nKey="lbrothers:description.desktop">
              <Description>
                Having managed over 500 + properties since 2010, and a current
                management portfolio in excess of $250 million, brothers Leo and
                Leonard are metro Vancouver&apos;s leading management duo.
                <br />
                <br />
                Utilizing comprehensive pre-rental analysis to optimally price a
                property, coupled with an in-depth knowledge of market trends,
                the L Brothers Vancouver Group are experts in maximizing returns
                for any real estate asset or investment.
                <br />
                <br />
                Trading | Pre-Sale | Property Management
                <br />
                Licensed under Park Georgia Realty
              </Description>
            </Trans>
            <Trans i18nKey="lbrothers:contact.desktop">
              <ContactInfo>1-604-767-8865 | leojzheng@gmail.com</ContactInfo>
            </Trans>
            <LandlordsWrapper>
              <AvatarsWrapper>
                {profile.agents.map((agent, index) => (
                  <AvatarContainer key={index}>
                    <Image
                      src={agent.image}
                      alt=""
                      width={85}
                      height={85}
                      placeholder="blur"
                    />
                    <AvatarTextContainer>
                      <AvatarText>{agent.name}</AvatarText>
                      {agent.roles}
                    </AvatarTextContainer>
                  </AvatarContainer>
                ))}
              </AvatarsWrapper>
            </LandlordsWrapper>
          </ContentContainer>
        </Container>
      </BannerWrapper>

      <SectionTitle>{t('lbrothers:Our Portfolio')}</SectionTitle>
      {!displayMobile && (
        <QrCode
          id="qr-code"
          positionBottom={footerOnScreen}
          positionTop={listingsOnScreen}
        >
          <QrCodeImageWrapper>
            <Image
              src={lBrothersQr}
              alt="L Brothers QR Code"
              width={304}
              height={394}
              placeholder="blur"
            />
          </QrCodeImageWrapper>
        </QrCode>
      )}
    </>
  )
}

export default LBrothersProfile
